import { Helmet } from "react-helmet";
import { BlogHeader } from "@/components/BlogHeader";
import { Footer } from "@/components/Footer";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Brain,
  Sparkles,
  MessageCircle,
  History,
  RefreshCw,
  Clock,
  BookOpen,
  AlertCircle,
  Mail,
  Heart,
  Moon,
  Smile,
  Activity,
} from "lucide-react";
import { useKopfaiClickTracking } from "@/hooks/useKopfaiClickTracking";

const About = () => {
  const { trackClick } = useKopfaiClickTracking();

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Sobre | KopfAI Blog - Bem-Estar Mental</title>
        <meta
          name="description"
          content="Conheça o KopfAI Blog, nossa missão de democratizar informações sobre bem-estar mental e a assistente de IA baseada em TCC."
        />
      </Helmet>

      <BlogHeader />

      {/* Hero Section */}
      <section className="relative py-10 sm:py-20 px-3 sm:px-4 overflow-hidden bg-gradient-to-br from-primary/10 via-secondary/5 to-background">
        <div className="absolute inset-0 bg-grid-white/5 bg-[size:20px_20px]" />
        <div className="container mx-auto max-w-4xl relative z-10 px-2 sm:px-4">
          <div className="text-center space-y-3 sm:space-y-4">
            <h1 className="text-2xl sm:text-3xl md:text-5xl font-bold text-foreground">Sobre o KopfAI Blog</h1>
            <p className="text-base sm:text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto">
              A clareza que sua mente merece
            </p>
          </div>
        </div>
      </section>

      {/* Missão e Propósito */}
      <section className="py-8 sm:py-16 px-3 sm:px-4">
        <div className="container mx-auto max-w-4xl px-2 sm:px-4">
          <div className="flex flex-col sm:flex-row items-center justify-center mb-6 sm:mb-8 gap-2 sm:gap-4">
            <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-foreground">Nossa Missão</h2>
          </div>
          <Card className="border-primary/20">
            <CardContent className="pt-4 sm:pt-6 px-4 sm:px-6">
              <p className="text-base sm:text-lg text-muted-foreground leading-relaxed mb-3 sm:mb-4">
                O KopfAI Blog nasceu com o propósito de{" "}
                <strong>democratizar o acesso a informações de qualidade sobre bem-estar mental</strong>. Acreditamos
                que todos merecem ter clareza mental e ferramentas práticas para lidar com os desafios emocionais do dia
                a dia.
              </p>
              <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
                Nossa missão é <strong>promover autoconhecimento através de conteúdo científico acessível</strong>,
                baseado em evidências e apresentado de forma clara e empática. Queremos ajudar você a desenvolver
                resiliência emocional e encontrar equilíbrio mental, trazendo informação que realmente faz diferença na
                sua vida.
              </p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* O Que Você Vai Encontrar */}
      <section className="py-8 sm:py-16 px-3 sm:px-4 bg-muted/30">
        <div className="container mx-auto max-w-6xl px-2 sm:px-4">
          <div className="flex flex-col sm:flex-row items-center justify-center mb-6 sm:mb-12 gap-2 sm:gap-4">
            <BookOpen className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 text-primary" />
            <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-foreground">O Que Você Vai Encontrar</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            <Card className="hover:shadow-lg transition-shadow">
              <CardHeader className="pb-4">
                <Heart className="w-7 h-7 sm:w-8 sm:h-8 text-primary mb-2" />
                <CardTitle className="text-lg sm:text-xl">Ansiedade e Estresse</CardTitle>
                <CardDescription className="text-sm sm:text-base">
                  Estratégias comprovadas para gerenciar ansiedade, reduzir estresse e encontrar calma mental no seu dia
                  a dia.
                </CardDescription>
              </CardHeader>
            </Card>

            <Card className="hover:shadow-lg transition-shadow">
              <CardHeader className="pb-4">
                <Sparkles className="w-7 h-7 sm:w-8 sm:h-8 text-primary mb-2" />
                <CardTitle className="text-lg sm:text-xl">Meditação e Mindfulness</CardTitle>
                <CardDescription className="text-sm sm:text-base">
                  Técnicas de atenção plena e meditação para desenvolver presença, foco e paz interior.
                </CardDescription>
              </CardHeader>
            </Card>

            <Card className="hover:shadow-lg transition-shadow">
              <CardHeader className="pb-4">
                <Moon className="w-7 h-7 sm:w-8 sm:h-8 text-primary mb-2" />
                <CardTitle className="text-lg sm:text-xl">Sono e Recuperação</CardTitle>
                <CardDescription className="text-sm sm:text-base">
                  Dicas práticas para melhorar a qualidade do sono e promover recuperação física e mental adequada.
                </CardDescription>
              </CardHeader>
            </Card>

            <Card className="hover:shadow-lg transition-shadow">
              <CardHeader className="pb-4">
                <Smile className="w-7 h-7 sm:w-8 sm:h-8 text-primary mb-2" />
                <CardTitle className="text-lg sm:text-xl">Autocuidado</CardTitle>
                <CardDescription className="text-sm sm:text-base">
                  Práticas de autocuidado emocional, físico e mental para construir uma rotina saudável e sustentável.
                </CardDescription>
              </CardHeader>
            </Card>

            <Card className="hover:shadow-lg transition-shadow">
              <CardHeader className="pb-4">
                <Brain className="w-7 h-7 sm:w-8 sm:h-8 text-primary mb-2" />
                <CardTitle className="text-lg sm:text-xl">Exercícios baseados em TCC</CardTitle>
                <CardDescription className="text-sm sm:text-base">
                  Técnicas práticas baseadas na Terapia Cognitivo-Comportamental para ressignificar pensamentos e
                  emoções.
                </CardDescription>
              </CardHeader>
            </Card>

            <Card className="hover:shadow-lg transition-shadow">
              <CardHeader className="pb-4">
                <Activity className="w-7 h-7 sm:w-8 sm:h-8 text-primary mb-2" />
                <CardTitle className="text-lg sm:text-xl">Resiliência Emocional</CardTitle>
                <CardDescription className="text-sm sm:text-base">
                  Ferramentas para desenvolver força emocional, superar desafios e crescer com as adversidades.
                </CardDescription>
              </CardHeader>
            </Card>
          </div>
        </div>
      </section>

      {/* Conheça a KopfAI */}
      <section className="py-8 sm:py-16 px-3 sm:px-4">
        <div className="container mx-auto max-w-7xl px-2 sm:px-4">
          <div className="space-y-4 sm:space-y-6">
            <div className="text-center mb-6 sm:mb-8">
              <h2 className="text-2xl sm:text-3xl font-bold mb-2">Conheça a KopfAI</h2>
              <p className="text-base sm:text-lg text-muted-foreground px-2">
                <strong>Sua assistente de IA direcionada ao seu bem-estar mental</strong>
              </p>
            </div>

            <p className="text-base sm:text-lg text-muted-foreground leading-relaxed text-center max-w-3xl mx-auto px-2">
              A <strong>KopfAI</strong> é mais do que um chat de IA - é sua companheira de jornada rumo ao seu bem-estar
              mental. Desenvolvida com base nos princípios científicos da{" "}
              <strong>Terapia Cognitivo-Comportamental (TCC)</strong>, ela conversa com você, te ajuda a ter mais
              clareza sobre seus pensamentos e oferece ferramentas práticas e personalizadas para você cuidar da sua
              saúde mental todos os dias.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-8 my-6 sm:my-12">
              <Card className="border-primary/30 bg-gradient-to-br from-primary/5 to-secondary/5">
                <CardContent className="flex flex-col items-center text-center p-4 sm:p-6">
                  <MessageCircle className="w-10 h-10 sm:w-12 sm:h-12 text-primary mb-3 sm:mb-4" />
                  <h3 className="font-semibold text-base sm:text-lg mb-2">Sem conselhos prontos</h3>
                  <p className="text-xs sm:text-sm text-muted-foreground">
                    A KopfAI entende seu contexto e faz as perguntas certas para guiar você a encontrar suas próprias
                    respostas e soluções.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-primary/30 bg-gradient-to-br from-primary/5 to-secondary/5">
                <CardContent className="flex flex-col items-center text-center p-4 sm:p-6">
                  <History className="w-10 h-10 sm:w-12 sm:h-12 text-primary mb-3 sm:mb-4" />
                  <h3 className="font-semibold text-base sm:text-lg mb-2">Memória aprimorada</h3>
                  <p className="text-xs sm:text-sm text-muted-foreground">
                    Não é preciso repetir o contexto. A KopfAI conecta os pontos de conversas passadas para identificar
                    padrões que você não percebeu sozinho.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-primary/30 bg-gradient-to-br from-primary/5 to-secondary/5">
                <CardContent className="flex flex-col items-center text-center p-4 sm:p-6">
                  <RefreshCw className="w-10 h-10 sm:w-12 sm:h-12 text-primary mb-3 sm:mb-4" />
                  <h3 className="font-semibold text-base sm:text-lg mb-2">Interrompa o loop mental</h3>
                  <p className="text-xs sm:text-sm text-muted-foreground">
                    A KopfAI é treinada para detectar pensamentos negativos automáticos e ajudar você a reestruturá-los.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-primary/30 bg-gradient-to-br from-primary/5 to-secondary/5">
                <CardContent className="flex flex-col items-center text-center p-4 sm:p-6">
                  <Clock className="w-10 h-10 sm:w-12 sm:h-12 text-primary mb-3 sm:mb-4" />
                  <h3 className="font-semibold text-base sm:text-lg mb-2">Apoio 24/7</h3>
                  <p className="text-xs sm:text-sm text-muted-foreground">
                    A KopfAI está disponível a qualquer momento e em qualquer lugar, oferecendo apoio imediato quando
                    você precisar.
                  </p>
                </CardContent>
              </Card>
            </div>

            <div className="flex justify-center mt-6 sm:mt-8">
              <Button size="lg" className="text-base sm:text-lg px-6 sm:px-8" asChild>
                <a
                  href="https://kopfai.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackClick("about_page", null, "Conhecer a KopfAI")}
                >
                  Conhecer a KopfAI
                  <Sparkles className="ml-2 w-4 h-4 sm:w-5 sm:h-5" />
                </a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Avisos Legais */}
      <section className="py-8 sm:py-16 px-3 sm:px-4 bg-muted/30">
        <div className="container mx-auto max-w-4xl px-2 sm:px-4">
          <div className="flex flex-col sm:flex-row items-center justify-center mb-6 sm:mb-8 gap-2 sm:gap-4">
            <AlertCircle className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 text-destructive" />
            <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-foreground">Aviso Importante</h2>
          </div>
          <Card className="border-destructive/30">
            <CardContent className="pt-4 sm:pt-6 px-4 sm:px-6 space-y-3 sm:space-y-4">
              <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                <strong>Este blog oferece informações educacionais sobre bem-estar mental e saúde emocional.</strong>{" "}
                Todo o conteúdo é baseado em evidências científicas, mas tem caráter puramente informativo e
                educacional.
              </p>
              <div className="bg-destructive/10 border border-destructive/30 rounded-lg p-3 sm:p-4 my-3 sm:my-4">
                <ul className="space-y-2 sm:space-y-3 text-sm sm:text-base text-muted-foreground">
                  <li className="flex items-start">
                    <AlertCircle className="w-4 h-4 sm:w-5 sm:h-5 text-destructive mr-2 sm:mr-3 mt-0.5 flex-shrink-0" />
                    <span>
                      <strong>Não substitui diagnóstico ou tratamento profissional:</strong> As informações aqui
                      apresentadas não devem ser usadas para autodiagnóstico ou autotratamento.
                    </span>
                  </li>
                  <li className="flex items-start">
                    <AlertCircle className="w-4 h-4 sm:w-5 sm:h-5 text-destructive mr-2 sm:mr-3 mt-0.5 flex-shrink-0" />
                    <span>
                      <strong>Não é conselho médico:</strong> O conteúdo não substitui consultas, exames ou
                      acompanhamento com profissionais de saúde mental licenciados.
                    </span>
                  </li>
                </ul>
              </div>
              <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                O KopfAI Blog e seus criadores não se responsabilizam por qualquer decisão tomada com base
                exclusivamente nas informações aqui publicadas. Use este conteúdo como um complemento, nunca como
                substituto, ao cuidado profissional.
              </p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Contato */}
      <section id="contato" className="py-8 sm:py-16 px-3 sm:px-4 scroll-mt-20">
        <div className="container mx-auto max-w-4xl px-2 sm:px-4">
          <div className="flex flex-col sm:flex-row items-center justify-center mb-6 sm:mb-8 gap-2 sm:gap-4">
            <Mail className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 text-primary" />
            <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-foreground">Entre em Contato</h2>
          </div>
          <Card>
            <CardContent className="pt-4 sm:pt-6 px-4 sm:px-6 text-center space-y-4 sm:space-y-6">
              <p className="text-base sm:text-lg text-muted-foreground">
                Tem dúvidas, sugestões ou quer colaborar com o KopfAI Blog? Estamos aqui para ouvir você!
              </p>
              <div className="flex flex-col items-center space-y-3 sm:space-y-4">
                <div className="flex items-center space-x-2 sm:space-x-3 text-lg sm:text-xl">
                  <Mail className="w-5 h-5 sm:w-6 sm:h-6 text-primary" />
                  <a
                    href="mailto:contato@kopfai.com"
                    className="text-primary hover:text-primary/80 font-semibold transition-colors break-all"
                  >
                    contato@kopfai.com
                  </a>
                </div>
                <Button size="lg" asChild>
                  <a href="mailto:contato@kopfai.com">Enviar Email</a>
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default About;
